import { DayActivity } from '../components/ItineraryDay'

interface DayTemplate {
  theme: string
  activities: Omit<DayActivity, 'timeSlot'>[]
  // [0] = morning, [1] = afternoon, [2] = evening
}

interface DestinationData {
  keywords: string[]
  dayTemplates: DayTemplate[]
  transfers: {
    type: string
    destination: string
    duration: string
    description: string
  }[]
  tips: string[]
}

export const destinationDatabase: Record<string, DestinationData> = {
  'punta-cana': {
    keywords: [
      'punta cana',
      'bavaro',
      'dominican republic',
      'cap cana',
      'dominican',
      'bahia principe',
      'bávaro',
      'la romana',
      'santo domingo',
    ],
    tips: [
      'Make specialty restaurant reservations as early as possible after arrival.',
      'Bring reef-safe sunscreen, a waterproof phone pouch, and cash for tips on excursions.',
      'Alternate big excursion days with lighter resort days — the trip will feel much better.',
      'Pre-book Saona Island, Scape Park, and catamaran cruises for April travel.',
      'Choose a smaller or premium catamaran for a more refined experience.',
    ],
    dayTemplates: [
      // Day 1: Arrival + Settle In
      {
        theme: 'Arrival & Resort Discovery',
        activities: [
          {
            title: 'Airport Transfer & Resort Check-In',
            description:
              'Arrive at Punta Cana International Airport and enjoy a private or shared transfer to your resort. Check in, get oriented, and start soaking in the Caribbean atmosphere.',
            duration: '2 Hours',
            category: 'Transfer',
          },
          {
            title: 'Explore the Resort & Bávaro Beach',
            description:
              'Spend the afternoon discovering the resort grounds — pools, beach access, restaurants, and bars. Take your first dip in the turquoise waters of Bávaro Beach with its soft white sand.',
            duration: 'Half Day',
            category: 'Beach',
          },
          {
            title: 'Welcome Dinner & Resort Entertainment',
            description:
              'Enjoy your first dinner at the resort — a casual buffet or easy restaurant to settle in. Take an evening walk around the property, grab a cocktail, and catch the resort entertainment.',
            duration: '2.5 Hours',
            category: 'Dining',
          },
        ],
      },
      // Day 2: Beach Day + Spa
      {
        theme: 'Beach Relaxation & Spa Retreat',
        activities: [
          {
            title: 'Bávaro Beach Morning',
            description:
              'Start your vacation with a leisurely breakfast, then head to Bávaro Beach — one of the top reasons people come to Punta Cana. Swim in the calm turquoise water, take a long beach walk, and enjoy classic Caribbean scenery.',
            duration: 'Half Day',
            category: 'Beach',
          },
          {
            title: 'Spa Treatment & Poolside Relaxation',
            description:
              'After lunch at the resort, indulge in a spa treatment — massage, hydrotherapy, facial, or after-sun treatment. Spend the rest of the afternoon by a quieter pool or on your balcony.',
            duration: '3 Hours',
            category: 'Wellness',
          },
          {
            title: 'Specialty Restaurant Dinner & Live Music',
            description:
              "Dress up for dinner at one of the resort's specialty restaurants. Enjoy live music or a resort show afterward — the perfect way to ease into vacation mode.",
            duration: '3 Hours',
            category: 'Dining',
          },
        ],
      },
      // Day 3: Saona Island
      {
        theme: 'Saona Island Paradise Excursion',
        activities: [
          {
            title: 'Speedboat to Saona Island',
            description:
              'Early breakfast, then hotel pickup for the most iconic day trip in the Dominican Republic. Board a speedboat or catamaran and head to Saona Island — a protected nature reserve known for crystal-clear water, palm-lined beaches, and natural swimming pools.',
            duration: '2 Hours',
            category: 'Exclusive',
          },
          {
            title: 'Saona Island Beach, Natural Pool & Lunch',
            description:
              "Spend the day on Saona Island's pristine white-sand beaches. Wade through the famous natural swimming pool where starfish dot the sandy bottom. Enjoy a beachside buffet lunch with fresh seafood and an open bar. This is the postcard-perfect Caribbean experience.",
            duration: 'Full Day',
            category: 'Beach',
          },
          {
            title: 'Catamaran Return & Light Resort Dinner',
            description:
              'Return by catamaran with music and rum cocktails as the sun sets over the Dominican coastline. Keep the evening low-key with a light dinner at the resort and early relaxation.',
            duration: '3 Hours',
            category: 'Relaxation',
          },
        ],
      },
      // Day 4: Recovery Day
      {
        theme: 'Resort Recovery & Casual Fun',
        activities: [
          {
            title: 'Sleep In & Late Brunch',
            description:
              "After yesterday's full-day excursion, sleep in and enjoy a late breakfast or brunch. Take a gentle beach walk along Bávaro Beach as the morning sun warms the sand.",
            duration: '2 Hours',
            category: 'Relaxation',
          },
          {
            title: 'Resort Activities & Poolside Leisure',
            description:
              'Choose an easy resort activity — kayaking, paddleboarding, pool games, or a cocktail tasting. Or simply relax with a book by the pool. Luxury travel should include breathing room.',
            duration: 'Half Day',
            category: 'Leisure',
          },
          {
            title: 'Sunset Drinks & Specialty Dinner',
            description:
              "Watch the sunset with a premium cocktail, then enjoy a nice dinner reservation at one of the resort's upscale restaurants. Catch the evening resort entertainment.",
            duration: '3 Hours',
            category: 'Dining',
          },
        ],
      },
      // Day 5: Scape Park & Hoyo Azul
      {
        theme: 'Scape Park & Hoyo Azul Adventure',
        activities: [
          {
            title: 'Transfer to Scape Park at Cap Cana',
            description:
              'After breakfast, transfer to Scape Park in Cap Cana for a day of adventure and nature. Start with the stunning Hoyo Azul — a striking blue cenote surrounded by cliffs and tropical forest. One of the most photogenic natural attractions in the area.',
            duration: '3 Hours',
            category: 'Nature',
          },
          {
            title: 'Ziplining, Cave Exploration & Lagoon Swimming',
            description:
              "Get your adrenaline pumping with ziplining through the jungle canopy, then explore ancient caves with indigenous rock art and underground pools. Cool off with a swim in the park's natural lagoons and scenic walking paths.",
            duration: '4 Hours',
            category: 'Adventure',
          },
          {
            title: 'Relaxed Resort Evening',
            description:
              'Return to the resort by late afternoon. Shower and unwind, then enjoy a relaxed dinner. Keep the evening quiet after an active day — perhaps a walk on the beach under the stars.',
            duration: '3 Hours',
            category: 'Relaxation',
          },
        ],
      },
      // Day 6: Catamaran Cruise + Snorkeling
      {
        theme: 'Catamaran Cruise & Snorkeling',
        activities: [
          {
            title: 'Breakfast & Marina Transfer',
            description:
              'Enjoy breakfast, then transfer to the marina for your catamaran cruise. Choose a smaller or premium catamaran for a more refined experience rather than the biggest party boat.',
            duration: '1.5 Hours',
            category: 'Transfer',
          },
          {
            title: 'Snorkeling, Swimming & Coastal Cruise',
            description:
              "Sail along the Punta Cana coastline with snorkeling stops at coral reef areas where you'll see tropical fish and shallow reef systems. Swim in turquoise waters, enjoy an open bar, music, and scenic coastal views from the deck.",
            duration: '4 Hours',
            category: 'Adventure',
          },
          {
            title: 'Coco Bongo or Romantic Dinner',
            description:
              "Choose your evening: experience Punta Cana's famous Coco Bongo nightlife with Vegas-style acrobatics, music, and dancing — or opt for a romantic dinner at the resort. Both are excellent ways to cap off a fun day on the water.",
            duration: '3.5 Hours',
            category: 'Entertainment',
          },
        ],
      },
      // Day 7: Flexible Final Day
      {
        theme: 'Your Choice — Flexible Final Day',
        activities: [
          {
            title: 'Morning at Your Pace',
            description:
              'Sleep in or rise early for a final sunrise on Bávaro Beach. Enjoy a long, leisurely breakfast. This is your day to follow your energy — no schedule required.',
            duration: '2 Hours',
            category: 'Relaxation',
          },
          {
            title: 'Choose Your Adventure',
            description:
              'Pick one final experience: horseback riding along the beach at sunset, parasailing with panoramic coastal views, shopping for Dominican rum, cigars, larimar jewelry and coffee, or a cultural countryside tour with cacao and coffee tastings, cigar rolling, and visits to Dominican villages.',
            duration: '3 Hours',
            category: 'Adventure',
          },
          {
            title: 'Signature Farewell Dinner',
            description:
              'Make this your best dinner of the trip. Dress up and enjoy your favorite resort restaurant or book a special farewell experience. Toast to an unforgettable week in Punta Cana with premium Dominican rum.',
            duration: '3 Hours',
            category: 'Dining',
          },
        ],
      },
      // Day 8: Departure
      {
        theme: 'Departure Day',
        activities: [
          {
            title: 'Final Beach Walk & Easy Breakfast',
            description:
              'If time allows, take one last walk on Bávaro Beach and breathe in the Caribbean air. Enjoy a simple breakfast without rushing — keep the final morning peaceful.',
            duration: '1.5 Hours',
            category: 'Relaxation',
          },
          {
            title: 'Check Out & Airport Transfer',
            description:
              'Check out of the resort and board your transfer to Punta Cana International Airport. Consider a private transfer for a smoother, more comfortable departure experience.',
            duration: '1.5 Hours',
            category: 'Transfer',
          },
          {
            title: 'Safe Travels Home',
            description:
              'Depart Punta Cana with memories of turquoise waters, white sand beaches, Saona Island, and the warmth of Dominican hospitality. Until next time!',
            duration: '',
            category: 'Departure',
          },
        ],
      },
      // Extra days for longer stays
      {
        theme: 'Dominican Culture & Countryside',
        activities: [
          {
            title: 'Buggy Adventure Through the Countryside',
            description:
              "Go off-roading on a buggy or ATV tour through the Dominican countryside — rural trails, muddy roads, and small villages. Wear older clothes and sunglasses — you'll get dusty!",
            duration: '3 Hours',
            category: 'Adventure',
          },
          {
            title: 'Cacao, Coffee & Cigar Experience',
            description:
              'Visit a traditional Dominican farm to taste organic coffee, cacao, and mamajuana (a local herbal drink). Watch a cigar rolling demonstration and learn about rural Dominican life beyond the resort zone.',
            duration: '3 Hours',
            category: 'Culture',
          },
          {
            title: 'Local Dominican Dinner Experience',
            description:
              'Venture beyond the resort for an authentic Dominican dinner — fresh grilled seafood, tostones, "La Bandera" (the national dish of rice, beans, and meat), and local rum cocktails.',
            duration: '2.5 Hours',
            category: 'Gastronomy',
          },
        ],
      },
      {
        theme: 'Water Sports & Marine Life',
        activities: [
          {
            title: 'Parasailing Over the Caribbean',
            description:
              "Soar above the turquoise Caribbean waters with panoramic views of the Punta Cana coastline. A quick but unforgettable experience that's easy to combine with beach time.",
            duration: '1 Hour',
            category: 'Adventure',
          },
          {
            title: 'Dolphin Encounter & Marine Discovery',
            description:
              'Visit a well-reviewed marine program for a dolphin encounter. Choose a reputable operator that prioritizes animal welfare for a meaningful and respectful experience.',
            duration: '2 Hours',
            category: 'Nature',
          },
          {
            title: 'Beachside Bonfire & Stargazing',
            description:
              'End the day with a bonfire on the beach, grilled seafood, and stargazing over the Caribbean Sea. The lack of city lights makes Punta Cana surprisingly good for seeing constellations.',
            duration: '2.5 Hours',
            category: 'Exclusive',
          },
        ],
      },
      {
        theme: 'Deep-Sea Fishing & Relaxation',
        activities: [
          {
            title: 'Deep-Sea Fishing Charter',
            description:
              'Board a fishing charter for a premium half-day outing. Punta Cana is a strong fishing destination — common catches include marlin, mahi-mahi, tuna, and wahoo. All equipment and crew provided.',
            duration: 'Half Day',
            category: 'Sport',
          },
          {
            title: 'Indigenous Eyes Ecological Reserve',
            description:
              'Visit this quieter, more refined nature experience in Cap Cana — a protected reserve with walking trails, freshwater lagoons, peaceful scenery, and possible wildlife spotting. A gentle break from crowded excursions.',
            duration: '2.5 Hours',
            category: 'Nature',
          },
          {
            title: 'Couples Spa & Sunset Cocktails',
            description:
              'Indulge in a couples massage or beachfront spa treatment using local coconut and cacao-based products. Follow it with sunset cocktails and a romantic dinner.',
            duration: '3 Hours',
            category: 'Wellness',
          },
        ],
      },
      {
        theme: 'Santo Domingo Cultural Day Trip',
        activities: [
          {
            title: 'Early Transfer to Santo Domingo',
            description:
              'Depart early for a full-day cultural excursion to Santo Domingo, the capital of the Dominican Republic. The drive is long but worth it for history and architecture lovers.',
            duration: '3 Hours',
            category: 'Transfer',
          },
          {
            title: 'Colonial Zone, Cathedral & Museums',
            description:
              'Explore the UNESCO World Heritage Colonial Zone — the oldest European settlement in the Americas. Visit the historic cathedral, walk cobblestone streets lined with colorful buildings, browse museums, and soak in authentic Dominican culture.',
            duration: '5 Hours',
            category: 'Culture',
          },
          {
            title: 'Local Dinner in Santo Domingo & Return',
            description:
              'Enjoy dinner at a local restaurant in the Colonial Zone before the return transfer. Try traditional Dominican dishes in their most authentic setting.',
            duration: '4 Hours',
            category: 'Gastronomy',
          },
        ],
      },
      {
        theme: 'Altos de Chavón & La Romana',
        activities: [
          {
            title: 'Transfer to Altos de Chavón',
            description:
              'Head to the La Romana area to visit Altos de Chavón — a beautifully recreated Mediterranean-style village perched above the Chavón River.',
            duration: '2 Hours',
            category: 'Transfer',
          },
          {
            title: 'Art Galleries, River Views & Village Exploration',
            description:
              'Explore the charming cobblestone streets, art galleries, and stunning river views. The architecture and atmosphere feel more like the Italian Riviera than the Caribbean — a unique and upscale experience.',
            duration: '3 Hours',
            category: 'Culture',
          },
          {
            title: 'Elegant Dinner at Altos de Chavón',
            description:
              "Dine at one of the village's refined restaurants overlooking the river valley. The more upscale atmosphere makes this a perfect special-occasion evening.",
            duration: '2.5 Hours',
            category: 'Gastronomy',
          },
        ],
      },
      {
        theme: 'Monkeyland & Plantation Tour',
        activities: [
          {
            title: 'Monkeyland Squirrel Monkey Experience',
            description:
              'Visit Monkeyland for a fun, family-friendly excursion where you interact with playful squirrel monkeys in a controlled tropical environment. Great for photos and lighthearted fun.',
            duration: '2 Hours',
            category: 'Nature',
          },
          {
            title: 'Plantation Visit & Dominican Tastings',
            description:
              'Tour a working plantation to learn about Dominican agriculture — taste fresh tropical fruits, organic coffee, cacao, and the famous mamajuana herbal drink. Shop for local rum, cigars, and larimar jewelry.',
            duration: '3 Hours',
            category: 'Culture',
          },
          {
            title: 'Resort Pool Party & Evening Show',
            description:
              'Return to the resort for a fun afternoon by the pool, then enjoy the evening entertainment — themed shows, live music, and dancing under the Caribbean sky.',
            duration: '3 Hours',
            category: 'Entertainment',
          },
        ],
      },
    ],
    transfers: [
      {
        type: 'Private Car',
        destination: 'Punta Cana International Airport (PUJ)',
        duration: '25 min',
        description:
          'Luxury SUV transfer with complimentary water and cold towels. Book private for a smoother experience.',
      },
      {
        type: 'Shared Shuttle',
        destination: 'Saona Island Departure Point',
        duration: '1.5 hrs',
        description:
          'Hotel pickup included with most Saona Island excursions. Early morning departure.',
      },
      {
        type: 'Private Car',
        destination: 'Cap Cana (Scape Park & Hoyo Azul)',
        duration: '20 min',
        description:
          'Short transfer to the Cap Cana area for Scape Park, Hoyo Azul, and the marina.',
      },
    ],
  },
}

// Find matching destination data by checking keywords against user input
export function findDestination(userInput: string): DestinationData | null {
  const input = userInput.toLowerCase().trim()
  for (const key of Object.keys(destinationDatabase)) {
    const dest = destinationDatabase[key]
    for (const keyword of dest.keywords) {
      if (input.includes(keyword) || keyword.includes(input)) {
        return dest
      }
    }
  }
  return null
}
